import { LitElement, html, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { EdrIncidentDetailsPanelEvents, TranslationsLabelsForEdrDetails } from '../types';
import { IncidentData } from '../../incidentsGrid/types';
import { getGeneralSectionIncidentData, getTriggerName } from '../../../utils/xdr.utils';
import { FontSizes } from '../../../types/common/types';
import _ from 'lodash';

/**
 * @summary Type here the description of this component
 * @tag edr-incident-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('edr-incident-details')
export class EdrIncidentDetailsWebComponent extends LitElement {
  @property({ attribute: false })
  incident: IncidentData;

  @property({ attribute: false })
  translations: TranslationsLabelsForEdrDetails;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2">${this.translations.incidentDetailsTitle}</gz-title>
      </div>
      <div class="content flex-1">
        ${this.renderGeneralSection()} ${this.renderAnalysisSection()} ${this.renderDetectionSection()}
        ${this.renderAttackInfo()} ${this.renderIncidentDetailsTechniquesSection()}
      </div>
      <div class="footer footer d-flex align-items-center">
        <gz-button @click="${this.showGraph}" buttonheight="medium" buttonfontsize="medium"
          >${this.translations.graphButtonText}</gz-button
        >
        <gz-button
          @click="${this.showEvents}"
          class="ml-2"
          type="quiet"
          color="secondary"
          buttonheight="medium"
          buttonfontsize="medium"
          >${this.translations.eventsButtonText}</gz-button
        >
      </div>
    `;
  }

  renderGeneralSection() {
    return html`<incident-general-section
      .title=${this.translations.generalSectionLabels.sectionTitle}
      .incident=${getGeneralSectionIncidentData(this.incident)}
      .translations=${this.translations.generalSectionLabels}
    >
    </incident-general-section>`;
  }

  renderAnalysisSection() {
    return html`<incident-details-analysis-section
      .status=${Translator.translateEdrIncidentStatus(this.incident.status)}
      .assignee=${this.incident.extra?.assigneeName}
      .priority=${Translator.translateEdrIncidentPriority(this.incident.priority)}
      .translations=${this.translations.analysisSectionLabels}
    >
    </incident-details-analysis-section>`;
  }

  renderDetectionSection() {
    return html`<incident-details-detections-section
      .triggerName=${getTriggerName(this.incident.specifics.trigger)}
      .alerts=${this.incident.specifics.alerts}
      .incident=${this.incident}
      .translations=${this.translations.detectionsSectionLabels}
    >
    </incident-details-detections-section>`;
  }

  renderAttackInfo() {
    return html` <incident-attack-info
      .translations=${this.translations.attackInfoSectionLabels}
      .actionTaken=${this.incident.extra?.actionTakenValue}
      .entities=${this.incident.entities}
      .alerts="${this.incident.alerts}"
      .killChainPhases=${this.incident.specifics?.killChainPhases}
      .incidentData=${this.incident}
      .operatingSystem=${this.incident.specifics?.endpoint?.operatingSystem}
      .artifacts=${this.getArtifacts()}
    >
    </incident-attack-info>`;
  }

  getArtifacts() {
    let numberOfArtifacts = 0;

    const counters = [
      'numberOfFiles',
      'numberOfProcesses',
      'numberOfDomains',
      'numberOfRegistries',
      'numberOfMails',
      'numberOfStorages',
    ];

    for (const counter of counters) {
      numberOfArtifacts += _.get(this.incident, `specifics.counters.${counter}`, 0);
    }

    return numberOfArtifacts;
  }

  renderIncidentDetailsTechniquesSection() {
    if (this.incident.mitreTags && this.incident.mitreTags.length > 0) {
      return html`<incident-details-techniques-section
        .mitreTags=${this.incident.mitreTags}
        .translations=${this.translations.techniquesSectionLabels}
      >
      </incident-details-techniques-section>`;
    }

    return nothing;
  }

  showGraph() {
    const event = new CustomEvent(EdrIncidentDetailsPanelEvents.SHOW_EDR_GRAPH, {
      detail: {
        incident: this.incident,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  showEvents() {
    const event = new CustomEvent(EdrIncidentDetailsPanelEvents.SHOW_EDR_EVENTS, {
      detail: {
        incident: this.incident,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edr-incident-details': EdrIncidentDetailsWebComponent;
  }
}
